import React from "react";
import PropTypes from "prop-types";

import isEmpty from "../../validation/is-empty";
import { Donut, Legend } from "britecharts-react";
import { comma } from "../../helpers/common.functions";
import { translate } from "../../utils/translation";
import {
  hasTaxInclusiveTotal,
  preferCalculatedTotalWithinRounding,
} from "../../helpers/costPlanningTotals.functions";

import "./PDCPDonutChart.css";

export default function PDCPDonutChart(props) {
  const { data } = props;
  const { width, height } = props;
  const { eRadius, iRadius } = props;
  const { lHeight, lWidth } = props;
  const { margin } = props;
  const { projectDashboard, colorSchema } = props;
  const { CP } = props;

  if (isEmpty(data)) {
    return null;
  }

  const maxObject = data.reduce((prev, current) => {
    return prev.value > current.value ? prev : current;
  });

  let ID = 0;

  if (!isEmpty(maxObject)) {
    ID = maxObject.id;
  }

  // Try and truncate text to prevent overflow.
  data.forEach((object) => {
    let displayName = object.name;
    const limit = 25;
    const elipsis = limit - 3;
    if (displayName.length > limit) {
      displayName = displayName.slice(0, elipsis) + "...";
    }
    object.name = displayName;
  });

  return (
    <div className="pdcp-donut-chart-container">
      <Donut
        isAnimated={false}
        colorSchema={colorSchema || null}
        data={data}
        width={width || 425}
        height={height || 200}
        externalRadius={eRadius || 90}
        internalRadius={iRadius || 70}
        highlightSliceById={ID}
        hasFixedHighlightedSlice={true}
      />
      <div className="pdcp-donut-chart-legend">
        <Legend
          colorSchema={colorSchema || null}
          data={data}
          height={lHeight || 120}
          width={lWidth || 365}
          numberFormat={","}
          highlightEntryById={ID}
          margin={margin || null}
        />
        {projectDashboard && (
          <ProjectGraphTotal
            data={data}
            calculatedTotal={CP.calculated_total}
            estimates={CP?.estimates}
          />
        )}
      </div>
    </div>
  );
}

const estimateShape = PropTypes.shape({
  project_cost: PropTypes.number,
  project_cost_tax: PropTypes.number,
  projectCost: PropTypes.number,
  projectCostTax: PropTypes.number,
});

PDCPDonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  width: PropTypes.number,
  height: PropTypes.number,
  eRadius: PropTypes.number,
  iRadius: PropTypes.number,
  lHeight: PropTypes.number,
  lWidth: PropTypes.number,
  margin: PropTypes.object,
  projectDashboard: PropTypes.bool,
  colorSchema: PropTypes.array,
  CP: PropTypes.shape({
    calculated_total: PropTypes.number,
    estimates: PropTypes.arrayOf(estimateShape),
  }),
};

function ProjectGraphTotal(props) {
  const { data } = props;
  const { calculatedTotal } = props;
  const { estimates } = props;

  let value = calculateTotal(data);
  value = preferCalculatedTotalWithinRounding(calculatedTotal, value);
  const label = hasTaxInclusiveTotal(estimates)
    ? "Project Cost (Incl. Tax)"
    : "Project Cost (Excl. Tax)";

  return (
    <div className="pdcp-donut-chart-total">
      <div className="pdcp-donut-chart-total-label">
        <b>{translate(label)}</b>
      </div>
      <div className="pdcp-donut-chart-total-value">
        <b>{comma(value)}</b>
      </div>
    </div>
  );
}

ProjectGraphTotal.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
    }),
  ),
  calculatedTotal: PropTypes.number,
  estimates: PropTypes.arrayOf(estimateShape),
};

function calculateTotal(selection) {
  let sum = 0;
  selection.forEach((item) => {
    sum += item.value;
  });
  return Math.round(sum);
}
